import React, { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck, ShieldAlert, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const VerifyIberdrolaPage = () => {
  const [nif, setNif] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCheck = async (e) => {
    e?.preventDefault();
    setError('');
    setResult(null);
    const clean = (nif || '').trim();
    if (clean.length < 9) {
      setError('NIF deve ter pelo menos 9 dígitos');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/tools/check-iberdrola/${clean}`);
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao verificar NIF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto p-6 space-y-6" data-testid="verify-iberdrola-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Verificar NIF — Iberdrola</h1>
          <p className="text-sm text-slate-500 mt-1">
            A Iberdrola só permite voltar a contratar um NIF passados <span className="font-semibold">6 meses e meio</span> (195 dias) desde a data do contrato Iberdrola anterior.
          </p>
        </div>

        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Search className="w-4 h-4 text-[#2D3E6F]" /> Pesquisa por NIF
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleCheck} className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="nif">NIF do cliente</Label>
                <Input
                  id="nif"
                  value={nif}
                  onChange={(e) => setNif(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  placeholder="123456789"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="iberdrola-nif-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="h-11 bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2 px-6"
                data-testid="iberdrola-check-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Verificar
              </Button>
            </form>

            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="iberdrola-error">{error}</p>
            )}

            {result && (
              <div
                className={`mt-6 rounded-xl border p-5 ${
                  result.eligible
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-red-50 border-red-200'
                }`}
                data-testid="iberdrola-result"
              >
                <div className="flex items-start gap-3">
                  {result.eligible ? (
                    <ShieldCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ShieldAlert className="w-8 h-8 text-red-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`text-lg font-bold ${result.eligible ? 'text-emerald-800' : 'text-red-800'}`}>
                      {result.eligible ? 'NIF Elegível' : 'NIF Não Elegível'}
                    </p>
                    <p className={`text-sm mt-1 ${result.eligible ? 'text-emerald-700' : 'text-red-700'}`}>
                      {result.message}
                    </p>
                  </div>
                </div>

                {result.last_contract_date && (
                  <div className="mt-4 grid grid-cols-2 gap-4 pt-4 border-t border-slate-200/60">
                    <div>
                      <p className="text-xs text-slate-500">Último contrato Iberdrola</p>
                      <p className="text-sm font-semibold text-slate-800">{result.last_contract_date}</p>
                      {result.last_contract_codigo && (
                        <p className="text-xs text-slate-400 mt-0.5">#{result.last_contract_codigo}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Disponível a partir de</p>
                      <p className="text-sm font-semibold text-slate-800">{result.eligible_at || '—'}</p>
                      {!result.eligible && result.days_remaining > 0 && (
                        <p className="text-xs text-red-600 mt-0.5">faltam {result.days_remaining} dias</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VerifyIberdrolaPage;
